import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { DRAW_TOOLS, type DrawTool, type ShapeKind } from '../doc/schema';
import type { CutoutMode } from './cutout';
import type { BrushKind, BrushSettings } from './paint/brushes';
import { DEFAULT_SHEET_DPI, DEFAULT_SHEET_PRESET } from './paint/presets';
import { effectiveTheme } from '../theme/theme';

/** Default colours offered everywhere a colour is picked. */
export const PALETTE = [
  '#111111',
  '#ffffff',
  '#8d8d8d',
  '#d7263d',
  '#f46036',
  '#ffd23f',
  '#3bb273',
  '#1b998b',
  '#2b5bd7',
  '#7b2cbf',
  '#ff6fb5',
  '#5b3a29',
] as const;

/** Modes of the draw tool: the ink tools, the eraser, the lasso that selects strokes, and the colour picker. */
export type PenMode = DrawTool | 'eraser' | 'lasso' | 'picker';
const PEN_MODES: readonly string[] = [...DRAW_TOOLS, 'eraser', 'lasso', 'picker'];

export const isInkMode = (mode: PenMode): mode is DrawTool => (DRAW_TOOLS as readonly string[]).includes(mode);

/** How one ink tool draws. Size is in screen pixels at the zoom the stroke is drawn at. */
export interface InkStyle {
  color: string;
  size: number;
  /** 0..1 */
  opacity: number;
}

/** Per-device tool defaults (persisted in localStorage, not part of the board). */
export interface ToolSettings {
  shapeKind: ShapeKind;
  fill: string;
  stroke: string;
  strokeWidth: number;
  penMode: PenMode;
  /** The ink tool used last: the one the colour picker picks for, and returns to. */
  inkTool: DrawTool;
  ink: Record<DrawTool, InkStyle>;
  eraserSize: number;
  /** The eraser removes every stroke it touches whole, instead of only the part under it. */
  eraserWhole: boolean;
  /** Pausing at the end of a line straightens it (Shift does too, in 15 degree steps). */
  holdStraighten: boolean;
  /** Stylus mode: a single finger never draws or moves things; two fingers still pan/zoom. */
  penOnly: boolean;
  /** Magnetic snapping to other items' edges and centres while moving/resizing (Alt bypasses). */
  snap: boolean;
  /** Also snap to the canvas grid. */
  snapGrid: boolean;
  /** Image imports: re-encode the stored original as lossy WebP (animated GIFs and attachments are left alone). */
  importWebp: boolean;
  /** WebP quality, 0..1. Dimensions are never changed by this; downscaling is a per-board setting. */
  importQuality: number;
  /** Cutout tool: outline shape, soft edge (world units) and whether the source keeps a hole. */
  cutoutMode: CutoutMode;
  cutoutFeather: number;
  cutoutPunch: boolean;
  /** Sketch sheet tool: the format of the next sheet placed. */
  sheetFormat: string;
  sheetDpi: number;
  sheetLandscape: boolean;
  sheetCustomW: number;
  sheetCustomH: number;
  /** Paper colour of new sheets, or 'none' for transparent. */
  sheetPaper: string;
  /** Painting on a sheet: the brush in hand, its colour, and each brush's own settings. */
  brush: BrushKind;
  paintColor: string;
  brushes: Record<BrushKind, BrushSettings>;
  /** Fill: colour distance tolerated, 0..255. */
  fillTolerance: number;
}

const BRUSH_BASE: BrushSettings = {
  size: 1,
  opacity: 1,
  hardness: 1,
  flow: 1,
  pressureSize: false,
  pressureOpacity: false,
  grain: 0,
  density: 0.5,
  pitch: 10,
  aliased: false,
};

export const DEFAULT_BRUSHES: Record<BrushKind, BrushSettings> = {
  pencil: { ...BRUSH_BASE, size: 6, grain: 0.6, pressureSize: true, pressureOpacity: true },
  fineliner: { ...BRUSH_BASE, size: 4 },
  brush: { ...BRUSH_BASE, size: 24, hardness: 0.5, flow: 0.8, pressureSize: true, pressureOpacity: true },
  pixel: { ...BRUSH_BASE, size: 1, aliased: true },
  halftone: { ...BRUSH_BASE, size: 80, hardness: 0.3, flow: 0.7, pressureOpacity: true },
  spray: { ...BRUSH_BASE, size: 80, flow: 0.6, pressureOpacity: true },
  eraser: { ...BRUSH_BASE, size: 24, hardness: 0.8, pressureSize: true },
  fill: { ...BRUSH_BASE },
  picker: { ...BRUSH_BASE },
  lasso: { ...BRUSH_BASE },
  move: { ...BRUSH_BASE },
};

const KEY = 'vds:tools';

// ink that is visible on the canvas of the theme in use when the settings were first created
const INK_COLOR = effectiveTheme() === 'dark' ? '#ececec' : '#111111';

const defaults: ToolSettings = {
  shapeKind: 'rect',
  fill: 'none',
  stroke: '#2b5bd7',
  strokeWidth: 3,
  penMode: 'pen',
  inkTool: 'pen',
  ink: {
    pen: { color: INK_COLOR, size: 4, opacity: 1 },
    fineliner: { color: INK_COLOR, size: 2, opacity: 1 },
    brush: { color: INK_COLOR, size: 8, opacity: 1 },
    highlighter: { color: '#ffd23f', size: 24, opacity: 1 },
    fill: { color: '#8d8d8d', size: 0, opacity: 1 },
  },
  eraserSize: 24,
  eraserWhole: false,
  holdStraighten: true,
  penOnly: false,
  snap: true,
  snapGrid: false,
  importWebp: true,
  importQuality: 0.9,
  cutoutMode: 'lasso',
  cutoutFeather: 0,
  cutoutPunch: false,
  sheetFormat: DEFAULT_SHEET_PRESET,
  sheetDpi: DEFAULT_SHEET_DPI,
  sheetLandscape: false,
  sheetCustomW: 2000,
  sheetCustomH: 2000,
  sheetPaper: '#ffffff',
  brush: 'pencil',
  paintColor: '#111111',
  brushes: DEFAULT_BRUSHES,
  fillTolerance: 32,
};

/** Settings saved before each ink tool had its own style kept the pen and highlighter flat. */
interface LegacyInk {
  penColor?: string;
  penSize?: number;
  highlighterColor?: string;
  highlighterSize?: number;
}

function load(): ToolSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const { penColor, penSize, highlighterColor, highlighterSize, ...saved } = JSON.parse(raw) as Partial<ToolSettings> & LegacyInk;
      const ink = {} as Record<DrawTool, InkStyle>;
      for (const t of DRAW_TOOLS) ink[t] = { ...defaults.ink[t], ...saved.ink?.[t] };
      if (!saved.ink) {
        if (penColor) ink.pen.color = penColor;
        if (penSize) ink.pen.size = penSize;
        if (highlighterColor) ink.highlighter.color = highlighterColor;
        if (highlighterSize) ink.highlighter.size = highlighterSize;
      }
      // brushes are nested: a brush added since the settings were stored keeps its defaults
      const brushes = { ...DEFAULT_BRUSHES } as Record<BrushKind, BrushSettings>;
      for (const k of Object.keys(DEFAULT_BRUSHES) as BrushKind[]) {
        brushes[k] = { ...DEFAULT_BRUSHES[k], ...(saved.brushes?.[k] ?? {}) };
      }
      // the pencil became graphite (tooth, pressure sets the darkness): one stored as the plain
      // hard round it used to be starts over with the new defaults
      if (saved.brushes?.pencil && saved.brushes.pencil.grain === undefined) brushes.pencil = { ...DEFAULT_BRUSHES.pencil };
      const merged = { ...defaults, ...saved, ink, brushes };
      if (!(merged.brush in DEFAULT_BRUSHES)) merged.brush = defaults.brush;
      if (!PEN_MODES.includes(merged.penMode)) merged.penMode = defaults.penMode;
      if (!saved.inkTool || !DRAW_TOOLS.includes(merged.inkTool)) merged.inkTool = isInkMode(merged.penMode) ? merged.penMode : defaults.inkTool;
      return merged;
    }
  } catch {
    /* ignore */
  }
  return defaults;
}

export const toolSettings = createStore<ToolSettings>(() => load());

toolSettings.subscribe((s) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
});

export function setToolSettings(patch: Partial<ToolSettings>): void {
  toolSettings.setState(patch);
}

/** Switches the draw tool's mode; an ink tool also becomes the one the colour picker serves. */
export function setPenMode(penMode: PenMode): void {
  toolSettings.setState(isInkMode(penMode) ? { penMode, inkTool: penMode } : { penMode });
}

export function setInkStyle(tool: DrawTool, patch: Partial<InkStyle>): void {
  const { ink } = toolSettings.getState();
  toolSettings.setState({ ink: { ...ink, [tool]: { ...ink[tool], ...patch } } });
}

export function useToolSettings<T>(selector: (s: ToolSettings) => T): T {
  return useStore(toolSettings, selector);
}

/** Changes one brush's settings. */
export function setBrushSettings(kind: BrushKind, patch: Partial<BrushSettings>): void {
  const cur = toolSettings.getState().brushes;
  toolSettings.setState({ brushes: { ...cur, [kind]: { ...cur[kind], ...patch } } });
}
